import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import moment from 'moment';
import Loader from '../../components/Loader';
import { get, post } from '../../services/ApiService';

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_WINDOW_DAYS = 30;

const shortDate = (value) => (value ? moment(value).format('D MMM YYYY') : 'Not recorded');
const dateInput = (value) => moment(value).format('YYYY-MM-DD');
const errorText = (error, fallback) => error?.response?.data?.error || fallback;

// The order ref and tracking number were already tried by the automatic join, so the search starts empty.
const initialFilters = (record) => ({
  search: '',
  product: '',
  from: dateInput(moment(record?.createdAt).subtract(DEFAULT_WINDOW_DAYS, 'days')),
  to: dateInput(new Date()),
});

const searchKeyOf = (item) => (item.ppid ? String(item.ppid) : item.title);

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success('Copied');
  } catch (error) {
    message.error('Could not copy. Select the text and copy it instead.');
  }
};

const OrderedItems = ({ items, onSearch }) => {
  if (items.length === 0) return <p className="returns-muted">No ordered items found for this return.</p>;
  return (
    <ul className="returns-ordered">
      {items.map((item, index) => (
        <li key={index}>
          <span className="returns-ordered-title">{item.title || item.sku || 'Untitled item'}</span>
          <span className="returns-ordered-meta">
            {item.ppid ? <span className="returns-ppid">pp{item.ppid}</span> : <span className="returns-muted">no PPID</span>}
            {item.quantity != null && <span className="returns-muted"> × {item.quantity}</span>}
          </span>
          <span className="returns-ordered-actions">
            <button type="button" className="returns-track" onClick={() => copyText(searchKeyOf(item))}>
              Copy
            </button>
            <button type="button" className="returns-track" onClick={() => onSearch(searchKeyOf(item))}>
              Search
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
};

const FindMatchModal = ({ record, onClose, onLinked }) => {
  const [filters, setFilters] = useState(initialFilters(null));
  const [items, setItems] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(null);

  useEffect(() => {
    if (!record) return undefined;
    setFilters(initialFilters(record));
    setItems([]);
    let cancelled = false;
    get(`api/returns/${record.returnId}/items`)
      .then((res) => { if (!cancelled) setItems(res.items || []); })
      .catch((error) => {
        if (!cancelled && !error?.handledByInterceptor) message.error(errorText(error, 'Could not load the ordered items'));
      });
    return () => { cancelled = true; };
  }, [record]);

  useEffect(() => {
    if (!record) return undefined;
    let cancelled = false;
    const handler = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await get(`api/returns/checkins/unmatched?${new URLSearchParams(filters)}`);
        if (!cancelled) setParcels(res.parcels || []);
      } catch (error) {
        if (!cancelled && !error?.handledByInterceptor) message.error(errorText(error, 'Could not search check-ins'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [record, filters]);

  const link = async (ref) => {
    setLinking(ref);
    try {
      await post(`api/returns/${record.returnId}/match`, { ref });
      message.success('Linked');
      onLinked();
      onClose();
    } catch (error) {
      if (!error?.handledByInterceptor) message.error(errorText(error, 'Could not link the check-in'));
    } finally {
      setLinking(null);
    }
  };

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const searchProduct = (value) => setFilters((f) => ({ ...f, product: value }));

  const renderResults = () => {
    if (loading) return <Loader />;
    if (parcels.length === 0) return <p className="returns-muted">No unmatched check-ins match these filters.</p>;
    return (
      <table>
        <thead>
          <tr>
            <th>Reference</th>
            <th>Returned</th>
            <th>Location</th>
            <th>Items</th>
            <th aria-label="Action" />
          </tr>
        </thead>
        <tbody>
          {parcels.map((parcel) => (
            <tr key={parcel.ref}>
              <td>{parcel.ref}</td>
              <td>{shortDate(parcel.dateReturned)}</td>
              <td>{parcel.location || 'Not recorded'}</td>
              <td>
                <ul className="returns-find-items">
                  {parcel.items.map((item, index) => (
                    <li key={index}>
                      {item.item}
                      {item.ppid && <span className="returns-muted"> (pp{item.ppid})</span>} × {item.quantity}
                      {item.condition ? `, ${item.condition}` : ''}
                    </li>
                  ))}
                </ul>
              </td>
              <td>
                <button
                  type="button"
                  className="returns-track"
                  onClick={() => link(parcel.ref)}
                  disabled={linking !== null}
                >
                  {linking === parcel.ref ? 'Linking…' : 'Link'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <Modal title="Find a check-in" open={Boolean(record)} onCancel={onClose} footer={null} width={760}>
      {record && (
        <div className="returns-checkin">
          <h4 className="returns-find-heading">Ordered items</h4>
          <OrderedItems items={items} onSearch={searchProduct} />

          <h4 className="returns-find-heading">Unmatched check-ins</h4>
          <div className="returns-find-filters">
            <label>
              Reference
              <input type="search" value={filters.search} onChange={setFilter('search')} placeholder="As typed by the warehouse" />
            </label>
            <label>
              Product
              <input type="search" value={filters.product} onChange={setFilter('product')} placeholder="Name or PPID" />
            </label>
            <label>
              From
              <input type="date" value={filters.from} onChange={setFilter('from')} />
            </label>
            <label>
              To
              <input type="date" value={filters.to} onChange={setFilter('to')} />
            </label>
          </div>
          {renderResults()}
        </div>
      )}
    </Modal>
  );
};

export default FindMatchModal;
